/**
 * Game Hub — category launcher shown after Home START.
 * Stacked sections (Play / Quests / Compete), each with independent horizontal scroll.
 */

import { fetchPracticeHubConfig } from "../lib/cloud";

export type GameHubSection = "play" | "quests" | "compete";

/** @deprecated Use GameHubSection — kept for splash-link call sites. */
export type GameHubTab = GameHubSection;

export type GameHubCategory =
    | "modes"
    | "practice"
    | "sandbox"
    | "multiplayer"
    | "local"
    | "challenges"
    | "tournaments"
    | "marathon"
    | "lootdrops"
    | "campaign"
    | "worldmap"
    | "soccer";

export type GameHubHooks = {
    showEl: (el: HTMLElement | null) => void;
    hideEl: (el: HTMLElement | null) => void;
    openModes: () => void;
    openMpEntry: () => void | Promise<void>;
    openLootDropEntry: () => void | Promise<void>;
    openLocalSetup: () => void;
    openPractice: () => void;
    openSandbox: () => void;
    openChallenges: () => void;
    openTournaments: () => void;
    openMarathon: () => void;
    openCampaign: () => void;
    openWorldMap: () => void;
    openSoccer: () => void;
    playScreenEnterMotion: () => void;
    playModeCardBeep: () => void;
    isTouchDevice: () => boolean;
};

let hooks: GameHubHooks | null = null;
let practiceShowInCategories = false;
let practiceHubConfigPromise: Promise<void> | null = null;
let hubTitleFitRaf = 0;
let hubTitleResizeObserver: ResizeObserver | null = null;

const gameHubScreen = () => document.getElementById("gameHubScreen");
const practiceScreen = () => document.getElementById("practiceScreen");
const challengesScreen = () => document.getElementById("challengesScreen");
const tournamentsScreen = () => document.getElementById("tournamentsScreen");
const marathonScreen = () => document.getElementById("marathonScreen");
const campaignScreen = () => document.getElementById("campaignScreen");
const worldMapScreen = () => document.getElementById("worldMapScreen");
const soccerScreen = () => document.getElementById("soccerScreen");
const sandboxBaseScreen = () => document.getElementById("sandboxBaseScreen");
const sandboxCommunityScreen = () => document.getElementById("sandboxCommunityScreen");
const gameHubLocalCard = () => document.getElementById("gameHubLocalCard");
const gameHubPracticeCard = () =>
    document.getElementById("gameHubPracticeCard") ||
    document.querySelector<HTMLElement>('#gameHubScreen [data-hub-category="practice"]');

const HUB_TITLE_MIN_PX = 10;

function fitHubCardTitle(title: HTMLElement): void {
    title.style.fontSize = "";
    title.style.letterSpacing = "";
    const baseSize = parseFloat(getComputedStyle(title).fontSize);
    if (!Number.isFinite(baseSize) || baseSize <= 0) return;
    let size = baseSize;
    title.style.fontSize = `${size}px`;
    // Shrink until the label fits on one line inside the card.
    while (title.scrollWidth > title.clientWidth + 0.5 && size > HUB_TITLE_MIN_PX) {
        size = Math.max(HUB_TITLE_MIN_PX, size - 0.5);
        title.style.fontSize = `${size}px`;
        if (size <= 12) title.style.letterSpacing = "0.02em";
        if (size <= HUB_TITLE_MIN_PX) break;
    }
}

export function fitGameHubCardTitles(): void {
    const screen = gameHubScreen();
    if (!screen) return;
    screen.querySelectorAll<HTMLElement>(".gameHubCard:not(.hidden) .gameHubCardTitle").forEach(fitHubCardTitle);
}

function scheduleFitGameHubCardTitles(): void {
    if (hubTitleFitRaf) cancelAnimationFrame(hubTitleFitRaf);
    hubTitleFitRaf = requestAnimationFrame(() => {
        hubTitleFitRaf = 0;
        fitGameHubCardTitles();
    });
}

function isGameHubSection(value: string | null | undefined): value is GameHubSection {
    return value === "play" || value === "quests" || value === "compete";
}

/** Scroll the hub to a stacked section (splash links / deep links). */
export function setGameHubTab(section: GameHubSection): void {
    scrollToGameHubSection(section);
}

export function scrollToGameHubSection(section: GameHubSection): void {
    if (!isGameHubSection(section)) return;
    const el =
        document.getElementById(
            section === "play"
                ? "gameHubSectionPlay"
                : section === "quests"
                  ? "gameHubSectionQuests"
                  : "gameHubSectionCompete",
        ) || document.querySelector<HTMLElement>(`#gameHubScreen [data-hub-section="${section}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
}

export function setPracticeHubVisible(show: boolean): void {
    practiceShowInCategories = !!show;
    const card = gameHubPracticeCard();
    if (card) card.classList.toggle("hidden", !practiceShowInCategories);
    scheduleFitGameHubCardTitles();
}

export function refreshHubPracticeCardVisibility(): void {
    setPracticeHubVisible(practiceShowInCategories);
}

export function refreshHubLocalCardVisibility(): void {
    const card = gameHubLocalCard();
    if (!card || !hooks) return;
    card.classList.toggle("hidden", !hooks.isTouchDevice());
    scheduleFitGameHubCardTitles();
}

export async function refreshHubCategoryVisibility(): Promise<void> {
    if (!practiceHubConfigPromise) {
        practiceHubConfigPromise = (async () => {
            try {
                const hub = await fetchPracticeHubConfig();
                setPracticeHubVisible(hub.showInCategories);
            } catch {
                setPracticeHubVisible(false);
            } finally {
                practiceHubConfigPromise = null;
            }
        })();
    }
    await practiceHubConfigPromise;
}

export function isGameHubVisible(): boolean {
    const el = gameHubScreen();
    return !!(el && !el.classList.contains("hidden"));
}

export function isHubPlaceholderVisible(): boolean {
    return false;
}

export function hideHubScreens(): void {
    hooks?.hideEl(gameHubScreen());
    hooks?.hideEl(practiceScreen());
    hooks?.hideEl(challengesScreen());
    hooks?.hideEl(tournamentsScreen());
    hooks?.hideEl(marathonScreen());
    hooks?.hideEl(campaignScreen());
    hooks?.hideEl(worldMapScreen());
    hooks?.hideEl(soccerScreen());
    hooks?.hideEl(sandboxBaseScreen());
    hooks?.hideEl(sandboxCommunityScreen());
}

function openHubCategory(category: GameHubCategory): void {
    if (!hooks) return;
    hooks.playModeCardBeep();

    switch (category) {
        case "modes":
            hooks.openModes();
            return;
        case "practice":
            hooks.openPractice();
            return;
        case "sandbox":
            hooks.openSandbox();
            return;
        case "multiplayer":
            hideHubScreens();
            void hooks.openMpEntry();
            return;
        case "local":
            hideHubScreens();
            hooks.openLocalSetup();
            return;
        case "challenges":
            hideHubScreens();
            hooks.hideEl(document.getElementById("modeScreen"));
            hooks.openChallenges();
            return;
        case "tournaments":
            hideHubScreens();
            hooks.hideEl(document.getElementById("modeScreen"));
            hooks.openTournaments();
            return;
        case "marathon":
            hideHubScreens();
            hooks.hideEl(document.getElementById("modeScreen"));
            hooks.openMarathon();
            return;
        case "campaign":
            hideHubScreens();
            hooks.hideEl(document.getElementById("modeScreen"));
            hooks.openCampaign();
            return;
        case "worldmap":
            // Visually disabled in hub categories; keep wiring for later re-enable.
            return;
        case "lootdrops":
            hideHubScreens();
            void hooks.openLootDropEntry();
            return;
        case "soccer":
            hideHubScreens();
            hooks.hideEl(document.getElementById("modeScreen"));
            hooks.openSoccer();
            return;
    }
}

function bindHubCard(card: HTMLElement): void {
    card.setAttribute("role", "button");
    if (!card.hasAttribute("tabindex")) card.tabIndex = 0;
    card.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        if (card.classList.contains("hidden")) return;
        const category = card.getAttribute("data-hub-category") as GameHubCategory | null;
        if (category) openHubCategory(category);
    });
}

function bindSectionHorizontalWheel(scrollEl: HTMLElement): void {
    scrollEl.addEventListener(
        "wheel",
        (e) => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
            if (scrollEl.scrollWidth <= scrollEl.clientWidth + 1) return;
            e.preventDefault();
            scrollEl.scrollLeft += e.deltaY;
        },
        { passive: false },
    );
}

export function mountGameHub(h: GameHubHooks): void {
    hooks = h;
    refreshHubLocalCardVisibility();
    refreshHubPracticeCardVisibility();
    void refreshHubCategoryVisibility();
    window.addEventListener("resize", () => {
        refreshHubLocalCardVisibility();
        scheduleFitGameHubCardTitles();
    }, { passive: true });

    const screen = gameHubScreen();
    screen?.querySelectorAll<HTMLElement>(".gameHubSectionScroll").forEach(bindSectionHorizontalWheel);

    if (typeof ResizeObserver !== "undefined" && screen) {
        hubTitleResizeObserver?.disconnect();
        hubTitleResizeObserver = new ResizeObserver(() => scheduleFitGameHubCardTitles());
        screen.querySelectorAll<HTMLElement>(".gameHubCard").forEach((card) => {
            hubTitleResizeObserver?.observe(card);
        });
    }
    scheduleFitGameHubCardTitles();

    screen?.addEventListener("click", (e) => {
        const card = (e.target as HTMLElement).closest<HTMLElement>("[data-hub-category]");
        if (!card || !screen.contains(card) || card.classList.contains("hidden")) return;
        const category = card.getAttribute("data-hub-category") as GameHubCategory | null;
        if (!category) return;
        openHubCategory(category);
    });

    screen?.querySelectorAll<HTMLElement>("[data-hub-category]").forEach(bindHubCard);
}
